import {
  type ColorValue,
  type NumericValue,
  type PropertyTypeValueMap,
  StyleType,
  wixColor,
  wixFont,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  ISettingsColor,
  ISettingsFont,
  ISiteTextPreset,
  IStyleParam,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';

export type CssColorValue = Parameters<typeof wixColor>[0];
export type CssFontValue = Parameters<typeof wixFont>[0];

type stringStyleParams<T extends string = string> = {
  fontStyleParam: boolean;
  value: T;
  // Optional: only the value produced by buildStringStyleParams carries it (so
  // the storybook FontPicker can read style.bold). Reader functions just read
  // `value`, so keeping it optional lets them accept the ISettingsFont that
  // styles.get() returns.
  style?: { bold: boolean; italic: boolean; underline: boolean };
};

export const buildStringStyleParams = <T extends string = string>(
  value: T,
): stringStyleParams<T> => {
  // `style` is never used for string-valued Font params, but tooling that
  // treats every Font param as a real font (e.g. the storybook styles-params
  // panel) reads `style.bold` and crashes without it.
  return {
    fontStyleParam: false,
    value,
    style: { bold: false, italic: false, underline: false },
  };
};

export const stringEnumParam = <T extends string>(
  defaultValue: T,
  type: StyleType,
): Omit<IStyleParam<StyleParamType.Font, ISettingsFont>, 'name'> => ({
  type: StyleParamType.Font,
  getDefaultValue: () => buildStringStyleParams(defaultValue as T),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      value: defaultValue as any,
    },
  } as NonNullable<IStyleParam['metadata']>,
});

export const stringParam = (
  defaultValue: string,
  type: StyleType,
): Omit<IStyleParam<StyleParamType.Font, ISettingsFont>, 'name'> => ({
  type: StyleParamType.Font,
  getDefaultValue: () => buildStringStyleParams(defaultValue),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
  } as NonNullable<IStyleParam['metadata']>,
});

export const getStringStyleParamsValue = (
  stringStyleParamObject: stringStyleParams,
) => {
  return stringStyleParamObject?.value;
};

export const getRadiusCssValue = (
  stringStyleParamObject: stringStyleParams,
): string =>
  (getStringStyleParamsValue(stringStyleParamObject) || '0 0 0 0')
    .split(' ')
    .map((v) => `${v}px`)
    .join(' ');

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<
    PropertyTypeValueMap[K]
  > extends Required<ValueType>
    ? K
    : never]: K;
};

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

export const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
  aiDescription?: string,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
    ...(aiDescription && { ai: { description: aiDescription } }),
  } as NonNullable<IStyleParam['metadata']>,
});

export const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  } as NonNullable<IStyleParam['metadata']>,
});

export const numberParamWithMobileDefault = (
  type: StyleTypeFor<NumericValue>,
  mobileDefaultValue: number,
  defaultValue: number,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ isMobile }) =>
    isMobile ? mobileDefaultValue : defaultValue,
  inheritDesktop: false,
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
    mobileDefaultValue: {
      type: 'static',
      value: mobileDefaultValue,
    },
  } as NonNullable<IStyleParam['metadata']>,
});

type BooleanStyleParam = Omit<
  IStyleParam<StyleParamType.Boolean, boolean>,
  'name'
>;

export const booleanParam = (defaultValue: boolean): BooleanStyleParam => ({
  type: StyleParamType.Boolean,
  getDefaultValue: () => defaultValue,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
  } as NonNullable<IStyleParam['metadata']>,
});

export const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  } as NonNullable<IStyleParam['metadata']>,
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

export const fontParam = (
  preset: keyof ISiteTextPreset,
  props: { size?: number; htmlTag?: string },
  themeFont: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, props),
  metadata: {
    type: StyleType.font,
    defaultValue: wixFont(themeFont),
  } as NonNullable<IStyleParam['metadata']>,
});
